/**
 * A MapEngine façade for Widget Host and controller tools while the BIM view is on screen. Camera, pointer, project,
 * capture and draw go through BimApi; layer and basemap members are deliberate no-ops (the BIM has no 2D layers).
 */
import type {
  Bounds,
  CameraState,
  CameraTarget,
  ClickEvent,
  DrawController,
  DrawHandlers,
  DrawMode,
  DrawnGeometry,
  EditHandlers,
  LngLat,
  MapEngine,
  OverlayFeature,
  OverlayGeometry,
  OverlayStyle,
  PickHandlers,
} from '@mighty-map/map-engine';
import { useEffect, useRef } from 'react';
import { sharedFromPose, poseFromShared } from '../../lib/bim/camera';
import { useViewMode } from '../../state/viewMode';
import { useBim, type BimApi } from './bimStore';

const MEASURE_COLOUR = 0xffb300;
const HIGHLIGHT_COLOUR = 0x00e5ff;
const SKETCH_COLOUR = 0xe91e63;
const DRAW_SKETCH = 'mm-draw-sketch';
const OVERLAY_PREFIX = 'mm-overlay-';

type Ident = { lat: number; lon: number; height: number };

export interface BimToolEngine extends MapEngine {
  /** The model height (AHD) under the pointer on the last move; null when off the canvas. */
  pointerHeight(): number | null;
}

type DrawSession =
  | {
      kind: 'draw';
      mode: DrawMode;
      handlers: DrawHandlers;
      points: [number, number, number][];
      heights: number[];
    }
  | { kind: 'pick'; handlers: PickHandlers }
  | { kind: 'edit'; handlers: EditHandlers; geometry: OverlayGeometry };

function styleColour(style: OverlayStyle): number {
  if (style === 'measure') return MEASURE_COLOUR;
  if (style === 'sketch') return SKETCH_COLOUR;
  return HIGHLIGHT_COLOUR;
}

function toGeometry(mode: DrawMode, pts: [number, number][]): DrawnGeometry | null {
  if (!pts.length) return null;
  if (mode === 'point') return { type: 'Point', coordinates: pts[0]! };
  if (mode === 'line') return pts.length >= 2 ? { type: 'LineString', coordinates: pts } : null;
  if (pts.length < 3) return pts.length >= 2 ? { type: 'LineString', coordinates: pts } : null;
  return { type: 'Polygon', coordinates: [[...pts, pts[0]!]] };
}

function heightAt(api: BimApi, lat: number, lon: number, fallback = 0): number {
  return api.sampleHeight(lat, lon) ?? fallback;
}

export function createBimToolEngine(api: BimApi): BimToolEngine {
  const clickHandlers = new Set<(e: ClickEvent) => void>();
  const moveHandlers = new Set<(lngLat: LngLat | null) => void>();
  const camHandlers = new Set<(c: CameraState) => void>();
  let lastIdent: Ident | null = null;
  let session: DrawSession | null = null;
  let lastPoseKey = '';
  let raf = 0;
  const overlays = new Map<string, { features: readonly OverlayFeature[]; style: OverlayStyle }>();

  const screenPx = (): number => api.canvas.clientHeight || window.innerHeight;

  const cameraNow = (): CameraState => {
    const p = api.getPose();
    if (!p) return { center: [0, 0], zoom: 14, bearing: 0, pitch: 0 };
    const s = sharedFromPose(p, screenPx());
    return { center: [s.center[0], s.center[1]], zoom: s.zoom, bearing: s.bearing, pitch: s.pitch };
  };

  const poseKey = (): string => {
    const p = api.getPose();
    return p ? `${p.lat},${p.lon},${p.dist},${p.heading},${p.pitch}` : '';
  };

  const tickCam = (): void => {
    raf = requestAnimationFrame(tickCam);
    if (!camHandlers.size) return;
    const k = poseKey();
    if (k === lastPoseKey) return;
    lastPoseKey = k;
    const c = cameraNow();
    camHandlers.forEach((h) => h(c));
  };

  const ensureCamLoop = (): void => {
    if (raf) return;
    lastPoseKey = poseKey();
    raf = requestAnimationFrame(tickCam);
  };

  const setClaim = (on: boolean): void => {
    useBim.getState().setClaim(on ? 'bim-tool-engine' : null);
  };

  const redrawSketch = (): void => {
    if (!session || session.kind !== 'draw') {
      api.setOverlay(DRAW_SKETCH, null);
      return;
    }
    // The session keeps [lon, lat, height]; the model's overlay takes [lat, lon, height].
    const pts = session.points.map(([lon, lat, h]) => [lat, lon, h] as [number, number, number]);
    if (!pts.length) {
      api.setOverlay(DRAW_SKETCH, null);
      return;
    }
    const mode = session.mode;
    if (mode === 'point') {
      api.setOverlay(DRAW_SKETCH, { points: [pts[0]!], kind: 'points', colour: SKETCH_COLOUR, size: 8 });
      return;
    }
    if (mode === 'line' || (mode === 'polygon' && pts.length < 3)) {
      api.setOverlay(DRAW_SKETCH, { points: pts, kind: 'line', colour: SKETCH_COLOUR, width: 2 });
      return;
    }
    if (mode === 'polygon') {
      api.setOverlay(DRAW_SKETCH, { points: pts, kind: 'loop', colour: SKETCH_COLOUR, width: 2 });
    }
  };

  const syncOverlays = (): void => {
    for (const [id, { features, style }] of overlays) {
      const colour = styleColour(style);
      const lines: [number, number, number][] = [];
      const loops: [number, number, number][] = [];
      const points: [number, number, number][] = [];
      for (const f of features) {
        const g = f.geometry;
        if (g.type === 'Point') {
          const [lon, lat] = g.coordinates;
          points.push([lat, lon, heightAt(api, lat, lon)]);
        } else if (g.type === 'LineString') {
          for (const [lon, lat] of g.coordinates) lines.push([lat, lon, heightAt(api, lat, lon)]);
        } else if (g.type === 'Polygon') {
          for (const [lon, lat] of g.coordinates[0] ?? []) loops.push([lat, lon, heightAt(api, lat, lon)]);
        }
      }
      const oid = OVERLAY_PREFIX + id;
      if (loops.length >= 3) api.setOverlay(oid, { points: loops, kind: 'loop', colour, width: 2 });
      else if (lines.length >= 2) api.setOverlay(oid, { points: lines, kind: 'line', colour, width: 2 });
      else if (points.length) api.setOverlay(oid, { points, kind: 'points', colour, size: 8 });
      else api.setOverlay(oid, null);
    }
  };

  const finishDraw = (s: Extract<DrawSession, { kind: 'draw' }>): void => {
    const ll = s.points.map(([lon, lat]) => [lon, lat] as [number, number]);
    let geometry: DrawnGeometry | null;
    if (s.mode === 'point') geometry = ll.length ? { type: 'Point', coordinates: ll[0]! } : null;
    else if (s.mode === 'line') geometry = ll.length >= 2 ? { type: 'LineString', coordinates: ll } : null;
    else {
      const ring =
        ll.length > 3 && ll[0]![0] === ll[ll.length - 1]![0] && ll[0]![1] === ll[ll.length - 1]![1]
          ? ll.slice(0, -1)
          : ll;
      geometry = toGeometry('polygon', ring);
      if (geometry?.type !== 'Polygon') geometry = null;
    }
    session = null;
    setClaim(false);
    unhookKeys();
    api.setOverlay(DRAW_SKETCH, null);
    if (!geometry) {
      s.handlers.onCancel?.();
      return;
    }
    s.handlers.onFinish?.(geometry);
  };

  const draw: DrawController = {
    start(mode, handlers) {
      draw.cancel();
      if (mode === 'circle' || mode === 'rectangle') {
        handlers.onCancel?.();
        return;
      }
      session = { kind: 'draw', mode, handlers, points: [], heights: [] };
      setClaim(true);
      hookKeys();
      redrawSketch();
    },
    edit(geometry, handlers) {
      draw.cancel();
      session = { kind: 'edit', handlers, geometry };
      setClaim(true);
      hookKeys();
    },
    pick(handlers) {
      draw.cancel();
      session = { kind: 'pick', handlers };
      setClaim(true);
      hookKeys();
    },
    finish() {
      const s = session;
      if (!s) return;
      if (s.kind === 'draw') finishDraw(s);
      else if (s.kind === 'edit') {
        session = null;
        setClaim(false);
        unhookKeys();
        s.handlers.onFinish?.(s.geometry);
      }
    },
    cancel() {
      const s = session;
      session = null;
      setClaim(false);
      unhookKeys();
      api.setOverlay(DRAW_SKETCH, null);
      if (s?.kind === 'draw' || s?.kind === 'edit' || s?.kind === 'pick') s.handlers.onCancel?.();
    },
    isActive() {
      return session !== null;
    },
    setOverlay(id, features, style) {
      overlays.set(id, { features, style });
      syncOverlays();
    },
    clearOverlay(id) {
      overlays.delete(id);
      api.setOverlay(OVERLAY_PREFIX + id, null);
    },
  };

  let moveRaf = 0;
  let pendingMove: { x: number; y: number } | null = null;

  const flushMove = (): void => {
    moveRaf = 0;
    const p = pendingMove;
    pendingMove = null;
    if (!p) {
      lastIdent = null;
      moveHandlers.forEach((h) => h(null));
      return;
    }
    const hit = api.identify(p.x, p.y);
    if (!hit) {
      lastIdent = null;
      moveHandlers.forEach((h) => h(null));
      return;
    }
    lastIdent = { lat: hit.lat, lon: hit.lon, height: hit.height };
    const ll: LngLat = [hit.lon, hit.lat];
    moveHandlers.forEach((h) => h(ll));
    const s = session;
    if (s?.kind === 'draw') s.handlers.onPointer?.(ll, false);
  };

  const onPointerMove = (e: PointerEvent): void => {
    pendingMove = { x: e.clientX, y: e.clientY };
    if (!moveRaf) moveRaf = requestAnimationFrame(flushMove);
  };

  let downAt: { x: number; y: number; t: number } | null = null;
  const onPointerDown = (e: PointerEvent): void => {
    if (e.button === 0) downAt = { x: e.clientX, y: e.clientY, t: Date.now() };
  };

  const onPointerUp = (e: PointerEvent): void => {
    const d = downAt;
    downAt = null;
    if (!d || e.button !== 0) return;
    if (Math.hypot(e.clientX - d.x, e.clientY - d.y) > 4 || Date.now() - d.t > 600) return;
    const hit = api.identify(e.clientX, e.clientY);
    const ll: LngLat = hit ? [hit.lon, hit.lat] : [0, 0];
    const point = { x: e.clientX, y: e.clientY };

    const s = session;
    if (s?.kind === 'pick') {
      s.handlers.onClick({ lngLat: ll, point, features: [] });
      return;
    }
    if (s?.kind === 'draw' && hit) {
      const pt: [number, number, number] = [hit.lon, hit.lat, hit.height];
      if (s.mode === 'point') {
        s.points = [pt];
        finishDraw(s);
        return;
      }
      s.points.push(pt);
      s.heights.push(hit.height);
      const open = s.points.map(([lon, lat]) => [lon, lat] as [number, number]);
      const first = open.length === 1 ? open[0] : undefined;
      s.handlers.onChange?.(first ? { type: 'Point', coordinates: first } : toGeometry(s.mode, open));
      redrawSketch();
      return;
    }

    if (!draw.isActive()) {
      const ev: ClickEvent = { lngLat: ll, point, features: [] };
      clickHandlers.forEach((h) => h(ev));
    }
  };

  const onDblClick = (e: MouseEvent): void => {
    const s = session;
    if (s?.kind === 'draw' && (s.mode === 'line' || s.mode === 'polygon') && s.points.length >= 2) {
      e.preventDefault();
      finishDraw(s);
    }
  };

  const onKey = (e: KeyboardEvent): void => {
    const s = session;
    if (!s) return;
    if (e.key === 'Escape') draw.cancel();
    if (e.key === 'Enter' && s.kind === 'draw') finishDraw(s);
  };

  const keyTarget: Window | typeof globalThis = api.canvas.ownerDocument?.defaultView ?? globalThis;
  let keysHooked = false;
  const hookKeys = (): void => {
    if (keysHooked) return;
    keysHooked = true;
    keyTarget.addEventListener('keydown', onKey);
  };
  const unhookKeys = (): void => {
    if (!keysHooked) return;
    keysHooked = false;
    keyTarget.removeEventListener('keydown', onKey);
  };

  const cv = api.canvas;
  cv.addEventListener('pointermove', onPointerMove);
  cv.addEventListener('pointerdown', onPointerDown);
  cv.addEventListener('pointerup', onPointerUp);
  cv.addEventListener('dblclick', onDblClick);

  const engine: BimToolEngine = {
    jumpTo(target: CameraTarget) {
      const cur = api.getPose();
      const lat = target.center?.[1] ?? cur?.lat ?? -32;
      const lon = target.center?.[0] ?? cur?.lon ?? 151;
      let dist = cur?.dist ?? 1200;
      let heading = cur?.heading ?? 0;
      let pitch = cur?.pitch ?? 0.5;
      if (target.zoom != null && Number.isFinite(target.zoom)) {
        const shared = {
          center: [lon, lat] as [number, number],
          zoom: target.zoom,
          bearing: target.bearing ?? 0,
          pitch: target.pitch ?? 0,
        };
        const p = poseFromShared(shared, screenPx(), useViewMode.getState().mode);
        if (p) {
          dist = p.dist;
          heading = p.heading;
          pitch = p.pitch;
        }
      }
      if (target.bearing != null) heading = -(target.bearing * Math.PI) / 180;
      if (target.pitch != null) pitch = Math.max(0.05, ((90 - target.pitch) * Math.PI) / 180);
      api.flyTo(lat, lon, dist, heading, pitch);
      ensureCamLoop();
    },
    getCamera: cameraNow,
    setBasemap() {
      /* no basemap in BIM */
    },
    setLayers() {
      /* no 2D layers */
    },
    setVisibility() {},
    swapSource() {},
    onClick(handler) {
      clickHandlers.add(handler);
      return () => clickHandlers.delete(handler);
    },
    onCameraChange(handler) {
      camHandlers.add(handler);
      ensureCamLoop();
      handler(cameraNow());
      return () => {
        camHandlers.delete(handler);
        if (!camHandlers.size && raf) {
          cancelAnimationFrame(raf);
          raf = 0;
        }
      };
    },
    onPointerMove(handler) {
      moveHandlers.add(handler);
      return () => moveHandlers.delete(handler);
    },
    fitBounds(bounds: Bounds, options?: { padding?: number; maxZoom?: number }) {
      const [w, s, e, n] = bounds;
      const lat = (s + n) / 2;
      const lon = (w + e) / 2;
      const span = Math.max(Math.abs(e - w), Math.abs(n - s));
      const zoom = Math.min(20, Math.max(4, 14 - Math.log2(span * 80)));
      const z = options?.maxZoom != null ? Math.min(zoom, options.maxZoom) : zoom;
      engine.jumpTo({ center: [lon, lat], zoom: z });
    },
    getBounds(): Bounds {
      const c = cameraNow();
      const [lon, lat] = c.center;
      const span = 0.02 / 2 ** (c.zoom - 14);
      return [lon - span, lat - span, lon + span, lat + span];
    },
    setOpacity() {},
    setLabelsVisible() {},
    project(lngLat) {
      const h = lastIdent && lastIdent.lon === lngLat[0] && lastIdent.lat === lngLat[1] ? lastIdent.height : undefined;
      const p = api.project(lngLat[1], lngLat[0], h);
      if (!p || p.behind) return { x: 0, y: 0 };
      const r = api.canvas.getBoundingClientRect();
      return { x: p.x - r.left, y: p.y - r.top };
    },
    capture() {
      const url = api.snapshot();
      return Promise.resolve(url ?? '');
    },
    draw,
    resize() {},
    destroy() {
      cv.removeEventListener('pointermove', onPointerMove);
      cv.removeEventListener('pointerdown', onPointerDown);
      cv.removeEventListener('pointerup', onPointerUp);
      cv.removeEventListener('dblclick', onDblClick);
      unhookKeys();
      if (raf) cancelAnimationFrame(raf);
      if (moveRaf) cancelAnimationFrame(moveRaf);
      draw.cancel();
      for (const id of overlays.keys()) api.setOverlay(OVERLAY_PREFIX + id, null);
      clickHandlers.clear();
      moveHandlers.clear();
      camHandlers.clear();
    },
    pointerHeight() {
      return lastIdent?.height ?? null;
    },
  };

  return engine;
}

/**
 * Map engine for tray/controller tools: the BIM façade while BIM is on screen, else the map/globe engine. `active`
 * false keeps the map/globe engine even in BIM, and makes no façade (it listens on the model's canvas while it lives).
 */
export function useToolEngine(mapEngine: MapEngine | null, active = true): MapEngine | null {
  const inBim = useViewMode((s) => s.mode === 'bim') && active;
  const api = useBim((s) => s.api);
  const ref = useRef<BimToolEngine | null>(null);
  const apiRef = useRef<BimApi | null>(null);

  useEffect(
    () => () => {
      ref.current?.destroy();
      ref.current = null;
      apiRef.current = null;
    },
    [],
  );

  if (!inBim || !api) {
    if (ref.current) {
      ref.current.destroy();
      ref.current = null;
      apiRef.current = null;
    }
    return mapEngine;
  }
  if (api !== apiRef.current) {
    ref.current?.destroy();
    ref.current = createBimToolEngine(api);
    apiRef.current = api;
  }

  return ref.current ?? mapEngine;
}

export function isBimToolEngine(e: MapEngine | null): e is BimToolEngine {
  return !!e && 'pointerHeight' in e && typeof (e as BimToolEngine).pointerHeight === 'function';
}
